// Community service — all Supabase operations for posts, comments, votes
// Replaces the localStorage-based community functions in storage.js

import { supabase } from './supabaseClient';

// ─── POSTS ────────────────────────────────────────────────────────────────────

export const fetchPosts = async () => {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .order('timestamp', { ascending: false })
    .limit(200);

  if (error) throw error;
  return data || [];
};

export const insertPost = async (post) => {
  const { data, error } = await supabase
    .from('posts')
    .insert([{
      id: post.id,
      user_id: post.userId,
      username: post.username,
      bio: post.bio || '',
      text: post.text,
      category: post.category,
      vibe: post.vibe,
      send_again: post.sendAgain,
      votes: 0,
      timestamp: post.timestamp,
      flags: 0,
      flagged_by: [],
      user_stats: post.userStats || {}
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const removePost = async (postId) => {
  const { error } = await supabase
    .from('posts')
    .delete()
    .eq('id', postId);

  if (error) throw error;
};

export const flagPostInDb = async (postId, userId) => {
  // Read current flaggedBy, add userId, update
  const { data: post, error: fetchErr } = await supabase
    .from('posts')
    .select('flags, flagged_by')
    .eq('id', postId)
    .single();

  if (fetchErr) throw fetchErr;

  if (post.flagged_by.includes(userId)) {
    throw new Error('Already flagged');
  }

  const { error } = await supabase
    .from('posts')
    .update({
      flags: post.flags + 1,
      flagged_by: [...post.flagged_by, userId]
    })
    .eq('id', postId);

  if (error) throw error;
};

// ─── VOTES ────────────────────────────────────────────────────────────────────

export const addVote = async (postId, userId) => {
  // Insert vote record
  const { error: voteErr } = await supabase
    .from('votes')
    .insert([{ post_id: postId, user_id: userId }]);

  if (voteErr) throw voteErr;

  // Increment post vote count
  const { data, error } = await supabase.rpc('increment_votes', { post_id: postId });
  if (error) {
    // Fallback: manual increment if RPC not available
    const { data: post } = await supabase
      .from('posts')
      .select('votes')
      .eq('id', postId)
      .single();
    await supabase
      .from('posts')
      .update({ votes: (post?.votes || 0) + 1 })
      .eq('id', postId);
  }
};

export const removeVote = async (postId, userId) => {
  // Delete vote record
  const { error: voteErr } = await supabase
    .from('votes')
    .delete()
    .eq('post_id', postId)
    .eq('user_id', userId);

  if (voteErr) throw voteErr;

  // Decrement post vote count
  const { data: post } = await supabase
    .from('posts')
    .select('votes')
    .eq('id', postId)
    .single();

  await supabase
    .from('posts')
    .update({ votes: Math.max(0, (post?.votes || 1) - 1) })
    .eq('id', postId);
};

export const fetchUserVotes = async (userId) => {
  const { data, error } = await supabase
    .from('votes')
    .select('post_id')
    .eq('user_id', userId);

  if (error) throw error;

  // Return as object map { postId: true } to match existing userVotes shape
  return (data || []).reduce((acc, v) => {
    acc[v.post_id] = true;
    return acc;
  }, {});
};

// ─── COMMENTS ─────────────────────────────────────────────────────────────────

export const fetchComments = async () => {
  const { data, error } = await supabase
    .from('comments')
    .select('*')
    .order('timestamp', { ascending: true });

  if (error) throw error;

  // Group by post_id to match existing comments state shape
  return (data || []).reduce((acc, c) => {
    const comment = {
      id: c.id,
      postId: c.post_id,
      userId: c.user_id,
      username: c.username,
      text: c.text,
      timestamp: c.timestamp,
      flags: c.flags,
      flaggedBy: c.flagged_by || []
    };
    if (!acc[c.post_id]) acc[c.post_id] = [];
    acc[c.post_id].push(comment);
    return acc;
  }, {});
};

export const insertComment = async (comment) => {
  const { data, error } = await supabase
    .from('comments')
    .insert([{
      id: comment.id,
      post_id: comment.postId,
      user_id: comment.userId,
      username: comment.username,
      text: comment.text,
      timestamp: comment.timestamp,
      flags: 0,
      flagged_by: []
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const removeComment = async (commentId) => {
  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId);

  if (error) throw error;
};

export const flagCommentInDb = async (commentId, userId) => {
  const { data: comment, error: fetchErr } = await supabase
    .from('comments')
    .select('flags, flagged_by')
    .eq('id', commentId)
    .single();

  if (fetchErr) throw fetchErr;

  if (comment.flagged_by.includes(userId)) {
    throw new Error('Already flagged');
  }

  const { error } = await supabase
    .from('comments')
    .update({
      flags: comment.flags + 1,
      flagged_by: [...comment.flagged_by, userId]
    })
    .eq('id', commentId);

  if (error) throw error;
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────

// Convert Supabase post row back to the shape the app expects
export const mapPost = (row) => ({
  id: row.id,
  userId: row.user_id,
  username: row.username,
  bio: row.bio || '',
  text: row.text,
  category: row.category,
  vibe: row.vibe,
  sendAgain: row.send_again,
  votes: row.votes,
  timestamp: row.timestamp,
  flags: row.flags,
  flaggedBy: row.flagged_by || [],
  userStats: row.user_stats || {}
});